import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { Subsection } from '../../data/sections';
import { Callout } from '../ui/Callout';
import { MediaPlaceholder } from '../ui/MediaPlaceholder';
import { SopPlaceholder } from '../ui/SopPlaceholder';
import { WorkflowDiagram } from '../ui/WorkflowDiagram';
interface SubsectionAccordionProps {
  subsection: Subsection;
  defaultOpen?: boolean;
}
export function SubsectionAccordion({
  subsection,
  defaultOpen = false
}: SubsectionAccordionProps) {
  const location = useLocation();
  const hash = location.hash.replace('#', '');
  const isHashTarget = hash === subsection.id;
  const [isOpen, setIsOpen] = useState(defaultOpen || isHashTarget);
  const [flash, setFlash] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // When hash points to this subsection, open + scroll + flash
  useEffect(() => {
    if (isHashTarget) {
      setIsOpen(true);
      // Wait a tick so accordion animates open, then scroll
      const t = setTimeout(() => {
        containerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
        setFlash(true);
        setTimeout(() => setFlash(false), 1600);
      }, 120);
      return () => clearTimeout(t);
    }
  }, [isHashTarget, location.key]);
  const hasContent = subsection.body || subsection.callouts && subsection.callouts.length > 0 || subsection.mediaSlots && subsection.mediaSlots.length > 0 || subsection.sopSlots && subsection.sopSlots.length > 0 || subsection.workflowDiagrams && subsection.workflowDiagrams.length > 0;
  return <div ref={containerRef} id={subsection.id} className={`mb-4 overflow-hidden rounded-xl border bg-card shadow-sm transition-all scroll-mt-24 ${flash ? 'ring-2 ring-primary border-primary/50' : 'hover:border-primary/30'}`} data-id="element-215">
      <button onClick={() => setIsOpen(!isOpen)} className="flex w-full items-center justify-between bg-card p-5 text-left transition-colors hover:bg-muted/50" data-id="element-216">
        <h3 className="text-lg font-semibold text-foreground" data-id="element-217">
          {subsection.title}
        </h3>
        <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-218" />
      </button>

      <AnimatePresence initial={false} data-id="element-219">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} data-id="element-220">
            <div className="border-t p-6" data-id="element-221">
              {hasContent ? <div className="space-y-6" data-id="element-222">
                  {/* Body Text */}
                  {subsection.body && <div className="prose prose-slate dark:prose-invert max-w-none" data-id="element-223">
                      {subsection.body.split('\n\n').map((paragraph, i) => {
                if (paragraph.startsWith('**') && paragraph.includes('**\n')) {
                  const parts = paragraph.split('**\n');
                  const header = parts[0].replace('**', '');
                  const list = parts[1];
                  return <div key={i} className="mb-4" data-id="element-224">
                              <strong className="block text-foreground mb-2" data-id="element-225">
                                {header}
                              </strong>
                              <div className="text-muted-foreground whitespace-pre-line pl-4 border-l-2 border-primary/20" data-id="element-226">
                                {list}
                              </div>
                            </div>;
                }
                return <p key={i} className="text-muted-foreground leading-relaxed whitespace-pre-line mb-4 last:mb-0" data-id="element-227">
                            {paragraph}
                          </p>;
              })}
                    </div>}

                  {subsection.callouts?.map((callout, i) => <Callout key={i} {...callout} data-id="element-228" />)}

                  {subsection.workflowDiagrams?.map((diagram, i) => <WorkflowDiagram key={i} {...diagram} data-id="element-229" />)}

                  {subsection.mediaSlots?.map((media, i) => <MediaPlaceholder key={i} {...media} data-id="element-230" />)}

                  {subsection.sopSlots?.map((sop, i) => <SopPlaceholder key={i} {...sop} data-id="element-231" />)}
                </div> : <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12 text-center" data-id="element-232">
                  <div className="rounded-full bg-muted p-3" data-id="element-233">
                    <svg className="h-6 w-6 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-234">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" data-id="element-235" />
                    </svg>
                  </div>
                  <h4 className="mt-4 font-medium text-foreground" data-id="element-236">
                    Content coming soon
                  </h4>
                  <p className="mt-1 text-sm text-muted-foreground max-w-sm" data-id="element-237">
                    Add SOPs, photos, videos, and operational documentation here
                    to build out this section.
                  </p>
                  <button className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90 transition-colors" data-id="element-238">
                    Edit Section
                  </button>
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}